import type { ActorRefFrom } from 'xstate';
import { createActor } from 'xstate';
import { getUploadPartsForStateMachine } from './getUploadParts';
import type {
  PartForStateMachine as Part,
  UploaderOptions,
  UploaderOptionsInput,
} from './types';
import { uploaderMachine } from './UploaderMachine';
import Store from './UploaderStore';
import { withErrorBoundary } from './utils/withErrorBoundary';

const awsMinPartSize = 5 * 1024 * 1024; // AWS multipart min file size

export class UploaderWithStateMachine {
  file!: File;

  private uploadUrls!: string[];

  private options: UploaderOptions;

  private uploaderMachine: ActorRefFrom<typeof uploaderMachine>;

  private store: Store;

  constructor(
    file: File,
    totalPartCount: number,
    uploadUrls: string[],
    options: UploaderOptionsInput
  ) {
    this.options = {
      meta: options.meta ?? {},
      minPartSize: options.minPartSize || awsMinPartSize,
      callbacks: Object.fromEntries(
        Object.entries(options.callbacks ?? {})
          .filter(([, callback]) => callback)
          .map(([key, callback]) => [key, withErrorBoundary(callback)])
      ),
    };

    Object.assign(this, {
      file,
      uploadUrls,
    });

    // initialize state machine here (will be in CREATED state)
    this.store = new Store(
      file,
      totalPartCount,
      this.options.callbacks?.onProgress
    );
    this.uploaderMachine = createActor(uploaderMachine, {
      input: {
        store: this.store,
        assetUrlLookupFn: options.assetUrlLookupFn,
        callbacks: this.options.callbacks,
        isLoggingEnabled: options.debug,
      },
    });

    this.uploaderMachine.start();
  }

  /**
   * Split up file into parts and queue the parts to be uploaded.
   * @returns {Uploader} - This instance.
   */
  private start = () => {
    // Split files into parts and attach meta object to them
    const allParts = getUploadPartsForStateMachine(
      this.file,
      this.uploadUrls,
      this.options.minPartSize,
      this.store.totalPartCount
    );

    const pendingParts: Part[] = allParts.map((part) => {
      return {
        ...part,
        bytesSent: 0,
      };
    });

    this.uploaderMachine.send({
      type: 'ADD_PARTS',
      parts: [...pendingParts],
    });
  };

  /**
   * Start or resume uploading the file.
   */
  upload = () => {
    if (this.isPaused()) {
      this.resume();
    } else {
      this.start();
    }
  };

  /**
   * Stop uploading data.
   */
  pause = () => {
    this.uploaderMachine.send({ type: 'PAUSE' });
  };

  /**
   * Cancel uploading data.
   */
  cancel = () => {
    this.uploaderMachine.send({ type: 'CANCEL' });
  };

  /**
   * Resume uploading parts that have not been completely uploaded.
   */
  private resume = () => {
    this.uploaderMachine.send({ type: 'RESUME' });
  };

  public isPaused() {
    return this.uploaderMachine.getSnapshot().matches({
      RUNNING: {
        UPLOADING_PARTS: 'PAUSED',
      },
    });
  }
}
